import { View } from '@adobe/react-spectrum';
import Share from '@react/react-spectrum/Icon/Share';
import Answer from '@spectrum-icons/workflow/Answer';
import CallCenter from '@spectrum-icons/workflow/CallCenter';
import Document from '@spectrum-icons/workflow/Document';
import InfoOutline from '@spectrum-icons/workflow/InfoOutline';
import WebPage from '@spectrum-icons/workflow/WebPage';
import React, { useContext } from 'react';
import { GridList } from 'react-aria-components';

import { DetailsSectionView } from '@exchange-frontends/components';
// Monorepo shared components
import { useIMS } from '@exchange-frontends/custom-hooks';

import { DetailHeading } from '../../../../../components/DetailHeading';
import { useStore } from '../../../store';
import { AppDetailsContext } from '../AppDetails';
import { CopyShareLink } from './CopyShareLink';
import GetSupport from './GetSupport';
import HelpSupportGridListItem from './HelpSupportGridListItem';
import InstallationOrDocumentation from './InstallationOrDocumentation';
import RedirectUrlToNewTab from './RedirectUrlToNewTab';
import ReportAbuse from './ReportAbuse';
import TrialInfo from './TrialInfo';

/**
 * Help Support List component
 * @component
 * @category Experience Cloud Details
 * @subcategory Details
 */
const SidebarHelpAndSupport = () => {
  const { cloud } = useContext(AppDetailsContext);
  const ims = useIMS();

  const detailScreenLoading = useStore((state) => state.detailScreenLoading);
  /** @type {import('@action-types/ecApp').AppDetails} */
  const detailScreen = useStore((state) => state.detailScreen || {});
  const { media, support, privacyPolicy, licenseAgreement, publisher, appType, trialInfo, description } = detailScreen;
  const { installInstructions, document } = media || {};
  const solutionWebsite = detailScreen?.solutionDetails?.solutionWebsite;
  const publisherWebsite = publisher?.website;
  const isSolution = appType === 'SLN';

  return (
    <>
      <DetailHeading level={4}>Help &amp; support</DetailHeading>
      <View marginBottom="size-400">
        <DetailsSectionView padding="size-0">
          <GridList aria-label="help and support" selectionMode="multiple">
            {(cloud.toLowerCase() === 'cc' || detailScreenLoading) && trialInfo?.linkedAppId && (
              <TrialInfo
                icon={<InfoOutline size="S" />}
                label="Trial Info"
                url={publisherWebsite}
                loading={detailScreenLoading}
                trialInfo={trialInfo}
                description={description}
              />
            )}
            {((publisherWebsite && cloud.toLowerCase() === 'cc') || detailScreenLoading) && (
              <RedirectUrlToNewTab
                icon={<WebPage size="S" />}
                label="Website"
                url={publisherWebsite}
                loading={detailScreenLoading}
              />
            )}
            {(installInstructions?.some((item) => item?.url || item?.name) || detailScreenLoading) && (
              <InstallationOrDocumentation
                icon={<Document size="S" />}
                label="Installation"
                details={installInstructions}
                loading={detailScreenLoading}
                type={cloud}
              />
            )}
            {(document?.length > 0 || detailScreenLoading) && (
              <InstallationOrDocumentation
                icon={<Document size="S" />}
                label="Documentation"
                details={document}
                loading={detailScreenLoading}
              />
            )}
            {!isSolution && (
              <GetSupport
                icon={<CallCenter size="S" />}
                label="Get support"
                details={support}
                loading={detailScreenLoading}
                type={cloud}
                appType={appType}
              />
            )}
            {((privacyPolicy && cloud.toLowerCase() === 'cc') || detailScreenLoading) && (
              <RedirectUrlToNewTab
                icon={<Document size="S" />}
                label="Privacy policy"
                url={appType === 'integrations' ? null : privacyPolicy}
                loading={detailScreenLoading}
              />
            )}
            {!ims?.isMock &&
              ims?.initialized &&
              ims?.isSignedInUser() &&
              cloud.toLowerCase() === 'cc' &&
              appType !== 'integrations' && (
                <ReportAbuse
                  icon={<Answer size="S" />}
                  label="Report Abuse"
                  loading={detailScreenLoading}
                  appType={appType}
                />
              )}
            {((licenseAgreement && cloud.toLowerCase() === 'cc') || detailScreenLoading) && (
              <RedirectUrlToNewTab
                icon={<Document size="S" />}
                label="Terms of use"
                url={licenseAgreement}
                loading={detailScreenLoading}
                appType={appType}
              />
            )}
            <CopyShareLink
              isDisabled={detailScreenLoading}
              text={window.location.href}
              icon={<Share size="S" />}
              label="Copy share link"
              loading={detailScreenLoading}
            />
            {solutionWebsite && (
              <HelpSupportGridListItem
                icon={<WebPage size="S" />}
                label="Solution website"
                dataLaunchId={`Help and support: Solution website`}
                onClick={() => window.open(solutionWebsite, '_blank', 'noopener,noreferrer')}
              />
            )}
          </GridList>
        </DetailsSectionView>
      </View>
    </>
  );
};

export default SidebarHelpAndSupport;
